import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Lenis from 'lenis';
import '@/App.css';
import ErrorBoundary from '@/components/ErrorBoundary';
import Preloader from '@/components/Preloader';
import CustomCursor from '@/components/CustomCursor';
import SEO from '@/components/SEO';
import Header from '@/components/Header';
import Hero from '@/components/Hero';
import About from '@/components/About';
import BringHere from '@/components/BringHere';
import Ticker from '@/components/Ticker';
import Services from '@/components/Services';
import Process from '@/components/Process';
import Thesis from '@/components/Thesis';
import Team from '@/components/Team';
import Ventures from '@/components/Ventures';
import Contact from '@/components/Contact';
import VentureDetail from '@/components/VentureDetail';
import { PostList, PostDetail } from '@/components/Journal';
import AdminPanel from '@/components/AdminPanel';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function HomePage({ showPreloader }) {
  const [preloaderDone, setPreloaderDone] = useState(!showPreloader);

  useEffect(() => {
    if (!preloaderDone) return;
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smooth: true,
      smoothTouch: false,
    });
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, [preloaderDone]);

  return (
    <>
      <SEO />
      {showPreloader && <Preloader onComplete={() => setPreloaderDone(true)} />}
      {preloaderDone && (
        <>
          <Header />
          <main>
            <Hero />
            <About />
            <BringHere />
            <Ticker />
            <Services />
            <Process />
            <Thesis />
            <Team />
            <Ventures />
            <Contact />
          </main>
        </>
      )}
    </>
  );
}

function VentureRoute() {
  return (
    <>
      <SEO title="Case Study" path="/ventures" />
      <VentureDetail />
    </>
  );
}

function AdminRoute() {
  return (
    <>
      <SEO title="Admin" path="/admin" />
      <AdminPanel />
    </>
  );
}

function AppRoutes() {
  const location = useLocation();
  const isFirstLoad = useState(true)[0];
  const isHome = location.pathname === '/';
  const isAdmin = location.pathname === '/admin';

  return (
    <>
      {!isAdmin && <CustomCursor />}
      {!isAdmin && <div className="grain-overlay" aria-hidden="true" />}
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage showPreloader={isFirstLoad && isHome} />} />
        <Route path="/ventures/:id" element={<VentureRoute />} />
        <Route path="/journal" element={<PostList />} />
        <Route path="/journal/:slug" element={<PostDetail />} />
        <Route path="/admin" element={<AdminRoute />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <AppRoutes />
        </ErrorBoundary>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
